import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../App";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import {
  Gift,
  Users,
  DollarSign,
  Wallet,
  ArrowRight,
  Copy,
  CheckCircle,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";

const ReferralPartnerWidget = () => {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [partnerCode, setPartnerCode] = useState("");
  const [copied, setCopied] = useState(false);

  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const fetchPartnerStats = async () => {
      if (!user || user.role !== "referral_partner") {
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API}/api/referrals/partner/dashboard`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.ok) {
          const data = await response.json();
          setStats(data.stats);
          setPartnerCode(data.partner?.referral_code || "");
        }
      } catch (error) {
        console.error("Request failed:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPartnerStats();
  }, [user, token, API]);

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(partnerCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Referral code copied!");
    } catch (error) {
      toast.error("Failed to copy");
    }
  };

  // Don't render if not a referral partner
  if (!user || user.role !== "referral_partner") {
    return null;
  }

  if (loading) {
    return (
      <Card className="card-modern bg-gradient-to-r from-rnb-navy to-rnb-blue text-white overflow-hidden">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="card-modern bg-gradient-to-r from-rnb-navy to-rnb-blue text-white overflow-hidden relative" data-testid="partner-widget">
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxjaXJjbGUgZmlsbD0icmdiYSgyNTUsMjU1LDI1NSwwLjEpIiBjeD0iMjAiIGN5PSIyMCIgcj0iMiIvPjwvZz48L3N2Zz4=')] opacity-30" />
      <CardContent className="p-6 relative">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg bg-white/10">
            <Gift className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-heading font-semibold text-lg">Partner Dashboard</h3>
            <p className="text-white/70 text-sm">Your referral stats at a glance</p>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <div className="p-3 bg-white/10 rounded-lg text-center">
            <Users className="h-4 w-4 mx-auto mb-1 text-white/70" />
            <p className="text-2xl font-bold">{stats?.total_referrals || 0}</p>
            <p className="text-xs text-white/70">Referrals</p>
          </div>
          <div className="p-3 bg-white/10 rounded-lg text-center">
            <CheckCircle className="h-4 w-4 mx-auto mb-1 text-green-400" />
            <p className="text-2xl font-bold">{stats?.confirmed_referrals || 0}</p>
            <p className="text-xs text-white/70">Confirmed</p>
          </div>
          <div className="p-3 bg-white/10 rounded-lg text-center">
            <DollarSign className="h-4 w-4 mx-auto mb-1 text-rnb-teal" />
            <p className="text-2xl font-bold">${stats?.total_earned || 0}</p>
            <p className="text-xs text-white/70">Earned</p>
          </div>
          <div className="p-3 bg-white/10 rounded-lg text-center">
            <Wallet className="h-4 w-4 mx-auto mb-1 text-amber-400" />
            <p className="text-2xl font-bold">${stats?.current_balance || 0}</p>
            <p className="text-xs text-white/70">Balance</p>
          </div>
        </div>

        {/* Referral Code */}
        <div className="flex items-center justify-between p-3 bg-white/10 rounded-lg mb-4">
          <div>
            <p className="text-xs text-white/70">Your Code</p>
            <p className="font-mono font-bold tracking-wider">{partnerCode}</p>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={copyCode}
            className="text-white hover:bg-white/10"
            data-testid="widget-copy-code"
          >
            {copied ? (
              <CheckCircle className="h-4 w-4" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
          </Button>
        </div>

        {/* CTA Button */}
        <Link to="/partner">
          <Button className="w-full bg-white text-rnb-navy hover:bg-gray-100" data-testid="widget-go-to-dashboard">
            Go to Dashboard
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
};

export default ReferralPartnerWidget;
